import { useRef, useState } from 'react';
import { formatFileSize } from '../../../../client/Juno/helpers';
import { Delete } from '@mui/icons-material';
import PropTypes from 'prop-types';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const FILE_SIZE_ERROR = 'File size exceeds the limit (5 MB)';
const FILE_TYPE_ERROR = 'File type not supported. Please upload SVG, PNG, JPG or GIF files only.';
const ALLOWED_FILE_TYPES = ['image/svg+xml', 'image/png', 'image/jpeg', 'image/gif'];

const Attachments = ({ attachments, setAttachments }) => {
    const [dragActive, setDragActive] = useState(false);
    const inputRef = useRef(null);

    const handleDrag = function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = function (e) {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFile(e.dataTransfer.files);
        }
    };

    const validateFile = file => {
        // if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        //     return FILE_TYPE_ERROR;
        // }

        // if (file.size > MAX_FILE_SIZE) {
        //     return FILE_SIZE_ERROR;
        // }

        return null;
    };

    const handleFile = files => {
        setAttachments(prev => ({ ...prev, error: '' }));
        for (let i = 0; i < files.length; i++) {
            const isFileValid = validateFile(files[i]);

            if (!isFileValid) {
                setAttachments(prev => ({
                    ...prev,
                    files: [...prev.files, files[i]],
                }));
            } else {
                setAttachments(prev => ({
                    ...prev,
                    error: isFileValid,
                }));
                setTimeout(() => {
                    setAttachments(prev => ({ ...prev, error: '' }));
                }, 2000);
            }
        }
    };

    const handleChange = e => {
        e.preventDefault();
        if (e.target.files && e.target.files[0]) {
            handleFile(e.target.files);
        }
    };

    const handleDelete = (event, i) => {
        setAttachments(prev => ({
            files: prev.files.filter((_, index) => index != i),
            error: '',
        }));
    };

    return (
        <div className="mb-4">
            <span className="uppercase text-sm font-medium text-balanceCard-manage tracking-wide text-left my-2 block">
                Attachments
            </span>
            <div className="rounded-md !border-2 !border-dashed border-[#A0A0AB] outline-none dark:border-[#70707B]">
                <div onDragEnter={handleDrag} className="b-color-black">
                    <input
                        ref={inputRef}
                        type="file"
                        id="input-file-upload"
                        multiple={true}
                        onChange={handleChange}
                        className="hidden"
                    />
                    <label
                        className={`h-full w-full flex items-center justify-center rounded-md bg-[#FCFCFC] p-4 cursor-pointer ${
                            dragActive ? 'drag-active' : ''
                        } dark:bg-[#FFFFFF0A]`}
                        htmlFor="input-file-upload">
                        <div>
                            <svg
                                className="mx-auto w-15 h-15 block"
                                width="60"
                                height="60"
                                viewBox="0 0 46 46"
                                fill="none">
                                <path
                                    d="M19.667 26.3333L23.0003 23M23.0003 23L26.3337 26.3333M23.0003 23V30.5M29.667 26.9524C30.6849 26.1117 31.3337 24.8399 31.3337 23.4167C31.3337 20.8854 29.2816 18.8333 26.7503 18.8333C26.5682 18.8333 26.3979 18.7383 26.3054 18.5814C25.2187 16.7374 23.2124 15.5 20.917 15.5C17.4652 15.5 14.667 18.2982 14.667 21.75C14.667 23.4718 15.3632 25.0309 16.4894 26.1613"
                                    stroke="var(--color-text)"
                                    strokeWidth="1.6"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                            <div className="text-sm text-center text-uppercase text-[#18181B] dark:text-[#FCFCFC]">
                                <button
                                    className="text-uppercase text-[#18181B] dark:text-[#FCFCFC]"
                                    data-e2e="upload-btn"
                                    type="button"
                                    onClick={() => inputRef.current.click()}>
                                    Click to upload
                                </button>{' '}
                                <span className="text-[#51525C] dark:text-[#D1D1D6]">
                                    or drag and drop
                                </span>
                            </div>
                        </div>
                    </label>
                    {dragActive && (
                        <div
                            id="drag-file-element"
                            onDragEnter={handleDrag}
                            onDragLeave={handleDrag}
                            onDragOver={handleDrag}
                            onDrop={handleDrop}></div>
                    )}
                </div>
            </div>
            {attachments.error ? <p className="errorMessage">{attachments.error}</p> : null}
            {attachments?.files?.length
                ? attachments.files.map((file, index) => (
                      <div
                          className="rounded-md border-1 border-[#D1D1D6] p-3.5 px-4 grid grid-cols-[1fr_auto] gap-2.5 items-center mt-3 dark:border-[#51525C]"
                          key={file.name}>
                          <div className="text-text uppercase text-sm">
                              <span data-e2e={file.name}>{file.name}</span>
                              <span className="block">{formatFileSize(file.size)}</span>
                          </div>

                          <Delete
                              sx={{
                                  color: 'transparent',
                                  stroke: 'var(--color-text)',
                                  strokeWidth: 2,
                              }}
                              className="cursor-pointer"
                              onClick={e => handleDelete(e, index)}
                          />
                      </div>
                  ))
                : null}
        </div>
    );
};

Attachments.propTypes = {
    attachments: PropTypes.shape({
        files: PropTypes.array.isRequired,
        error: PropTypes.string,
    }).isRequired,
    setAttachments: PropTypes.func.isRequired,
};

export default Attachments;
